import { docsBundle, findPrompt, findResource, findTool } from '../pages/mcp-docs/docsBundle';
import {
  beatLibraryBeatCount,
  beatLibraryBeatTypes,
  beatLibraryData,
  beatLibraryMovies,
  getBeatLibraryBeatType,
  getBeatLibraryMovie,
} from '../data/beatLibrary';

const SITE_ORIGIN = 'https://beatbandit.ai';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/social-preview.png`;

export interface PublicRouteMetadata {
  title: string;
  description: string;
  canonicalPath: string;
  robots: string;
  type: 'website' | 'article';
  image: string;
  structuredData: Record<string, unknown>;
}

function metadata(
  canonicalPath: string,
  title: string,
  description: string,
  options: { robots?: string; type?: 'website' | 'article'; structuredType?: string; image?: string } = {},
): PublicRouteMetadata {
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath === '/' ? '/' : canonicalPath}`;
  return {
    title,
    description,
    canonicalPath,
    robots: options.robots ?? 'index,follow,max-image-preview:large',
    type: options.type ?? 'website',
    image: options.image ?? DEFAULT_IMAGE,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': options.structuredType ?? 'WebPage',
      name: title,
      description,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: 'BeatBandit',
        url: SITE_ORIGIN,
      },
      image: options.image ?? DEFAULT_IMAGE,
    },
  };
}

const STATIC_PUBLIC_ROUTES: Record<string, PublicRouteMetadata> = {
  '/': {
    ...metadata(
      '/',
      'BeatBandit | You Have a Story. Turn It into a Screenplay.',
      'Every film you love was built on story beats. BeatBandit helps anyone turn an idea into a screenplay: plan the beats, develop characters and write formatted pages, with AI suggestions you review and approve.',
      { structuredType: 'SoftwareApplication' },
    ),
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'BeatBandit',
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web',
      description:
        'BeatBandit turns an idea into a structured, formatted screenplay: story development, scene lists, screenplay writing, revision, storyboards and exports.',
      url: `${SITE_ORIGIN}/`,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Pay per use with credit packs',
      },
      creator: { '@type': 'Person', name: 'Roope Rainisto' },
    },
  },
  '/pricing': metadata(
    '/pricing',
    'BeatBandit Pricing | Pay Per Use, No Subscription',
    'BeatBandit is pay per use: buy a pack of credits and spend them only when the AI works for you. No subscription, and credits never expire.',
  ),
  '/write': metadata(
    '/write',
    'Write a Screenplay with BeatBandit | How It Works',
    'How BeatBandit turns an idea into a screenplay: story cards that remember every decision, a causal scene list, pages written in your voice, and notes you can act on.',
  ),
  '/produce': metadata(
    '/produce',
    'From Screenplay to Storyboards and Video | BeatBandit Produce',
    'Turn a finished screenplay into a two-pass shot list, consistent character and location references, storyboards, sound and video from 27 models, then cut it on a timeline.',
    { image: `${SITE_ORIGIN}/samples/station-one/storyboard-scene9-shot7.webp` },
  ),
  '/samples': metadata(
    '/samples',
    'Sample Screenplays Written with BeatBandit | Read the Pages',
    'Read full screenplays, a TV pilot, a micro-drama and a novel made with BeatBandit, each with the short brief it started from.',
    { structuredType: 'CollectionPage' },
  ),
  '/library': metadata(
    '/library',
    'Movie Beat Sheets & Story Structure Examples | BeatBandit',
    `Study ${beatLibraryMovies.length} popular films through a flexible four-act beat map. Free, reviewed breakdowns trace ${beatLibraryBeatCount} major story beats from opening image to resolution.`,
    { structuredType: 'CollectionPage', image: `${SITE_ORIGIN}/library/beat-library-hero.webp` },
  ),
  '/mcp': metadata(
    '/mcp',
    'BeatBandit MCP Server | Connect AI Agents to Your Story Engine',
    `Connect ChatGPT, Claude, Cursor, Codex, and other MCP clients to ${docsBundle.counts.tools} BeatBandit story, screenplay, media, and export tools.`,
    { structuredType: 'TechArticle' },
  ),
  '/mcp/install': metadata(
    '/mcp/install',
    'Install the BeatBandit MCP Server | Client Setup and OAuth',
    'Connect BeatBandit to ChatGPT, Claude Code, Cursor, VS Code, Codex, or another Streamable HTTP MCP client using OAuth 2.1.',
    { structuredType: 'TechArticle' },
  ),
  '/mcp/support': metadata(
    '/mcp/support',
    'BeatBandit Support | Connections, Billing and Privacy',
    'Get help with BeatBandit connections, billing, privacy and content reports, or revoke a connected application.',
  ),
  '/mcp/tools': metadata(
    '/mcp/tools',
    'BeatBandit MCP Tools | Complete API Reference',
    `Browse all ${docsBundle.counts.tools} BeatBandit MCP tools for projects, story structure, screenplay editing, reviews, media, and exports.`,
    { structuredType: 'TechArticle' },
  ),
  '/mcp/prompts': metadata(
    '/mcp/prompts',
    'BeatBandit MCP Prompts | Agent Workflow Reference',
    `Browse ${docsBundle.counts.prompts} live-state-aware BeatBandit MCP prompts for screenplay, revision, and production workflows.`,
    { structuredType: 'TechArticle' },
  ),
  '/mcp/resources': metadata(
    '/mcp/resources',
    'BeatBandit MCP Resources | Project and Screenplay Data Reference',
    `Browse ${docsBundle.counts.resources} BeatBandit MCP resource families for project canon, screenplays, media, review results, and workflow outputs.`,
    { structuredType: 'TechArticle' },
  ),
  '/privacy': metadata(
    '/privacy',
    'Privacy Policy | BeatBandit',
    'Read the BeatBandit privacy policy, including how account, project, and service data is handled.',
  ),
  '/terms': metadata(
    '/terms',
    'Terms of Service | BeatBandit',
    'Read the terms governing use of the BeatBandit story development and screenwriting platform.',
  ),
  '/cookies': metadata(
    '/cookies',
    'Cookie Policy | BeatBandit',
    'Read how BeatBandit uses essential, functional, analytics, and other browser technologies.',
  ),
  '/return-policy': metadata(
    '/return-policy',
    'Return Policy | BeatBandit',
    'Read the BeatBandit return and refund policy for digital credits and services.',
  ),
};

const NOINDEX_PATH_PREFIXES = [
  '/account',
  '/admin',
  '/auth',
  '/credits',
  '/dashboard',
  '/debug',
  '/exports',
  '/invite',
  '/oauth',
  '/payment',
  '/project-invite',
  '/projects',
  '/reset-password',
  '/sign-in',
  '/sign-up',
];

const APPLICATION_ROUTE_TITLES: Array<[prefix: string, title: string]> = [
  ['/dashboard', 'Projects | BeatBandit'],
  ['/exports', 'Download Export | BeatBandit'],
  ['/projects', 'Story Project | BeatBandit'],
  ['/account', 'Account | BeatBandit'],
  ['/credits', 'Credits | BeatBandit'],
  ['/payment', 'Payment | BeatBandit'],
  ['/admin', 'Admin | BeatBandit'],
  ['/sign-in', 'Sign In | BeatBandit'],
  ['/sign-up', 'Sign Up | BeatBandit'],
  ['/reset-password', 'Reset Password | BeatBandit'],
  ['/oauth', 'Authorization | BeatBandit'],
  ['/auth', 'Signing In | BeatBandit'],
  ['/project-invite', 'Project Invitation | BeatBandit'],
  ['/invite', 'Invitation | BeatBandit'],
];

export function normalizePublicPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

export function getPublicRouteMetadata(pathname: string): PublicRouteMetadata | null {
  const path = normalizePublicPath(pathname);
  const exact = STATIC_PUBLIC_ROUTES[path];
  if (exact) return exact;

  const beatMatch = path.match(/^\/library\/beat\/([^/]+)$/);
  if (beatMatch) {
    const beatType = getBeatLibraryBeatType(decodeURIComponent(beatMatch[1]));
    if (beatType) {
      const canonicalUrl = `${SITE_ORIGIN}/library/beat/${beatType.id}`;
      const description = `${beatType.what} See how ${beatType.entries.length} popular films play the ${beatType.name} beat, side by side.`;
      return {
        ...metadata(
          `/library/beat/${beatType.id}`,
          `${beatType.name}: the Story Beat in ${beatType.entries.length} Films | BeatBandit`,
          description,
          { type: 'article', image: `${SITE_ORIGIN}/library/beat-library-hero.webp` },
        ),
        structuredData: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: `${beatType.name}: the story beat in ${beatType.entries.length} films`,
              description,
              url: canonicalUrl,
              dateModified: beatLibraryData.reviewedAt,
              author: { '@type': 'Organization', name: 'BeatBandit' },
              publisher: { '@type': 'Organization', name: 'BeatBandit', url: SITE_ORIGIN },
              image: `${SITE_ORIGIN}/library/beat-library-hero.webp`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Beat Library', item: `${SITE_ORIGIN}/library` },
                { '@type': 'ListItem', position: 2, name: beatType.name, item: canonicalUrl },
              ],
            },
          ],
        },
      };
    }
  }

  const libraryMatch = path.match(/^\/library\/([^/]+)$/);
  if (libraryMatch) {
    const movie = getBeatLibraryMovie(decodeURIComponent(libraryMatch[1]));
    if (movie) {
      const canonicalUrl = `${SITE_ORIGIN}/library/${movie.id}`;
      const description = `${movie.title} (${movie.year}) beat sheet: a reviewed four-act structural analysis tracing the film's major story beats from setup through resolution.`;
      return {
        ...metadata(
          `/library/${movie.id}`,
          `${movie.title} Beat Sheet & Story Structure | BeatBandit`,
          description,
          { type: 'article', image: `${SITE_ORIGIN}/library/beat-library-hero.webp` },
        ),
        structuredData: {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: `${movie.title} Beat Sheet & Story Structure`,
              description,
              url: canonicalUrl,
              dateModified: beatLibraryData.reviewedAt,
              author: { '@type': 'Organization', name: 'BeatBandit' },
              publisher: { '@type': 'Organization', name: 'BeatBandit', url: SITE_ORIGIN },
              image: `${SITE_ORIGIN}/library/beat-library-hero.webp`,
              about: { '@type': 'Movie', name: movie.title, dateCreated: String(movie.year) },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Beat Library', item: `${SITE_ORIGIN}/library` },
                { '@type': 'ListItem', position: 2, name: movie.title, item: canonicalUrl },
              ],
            },
          ],
        },
      };
    }
  }

  const toolMatch = path.match(/^\/mcp\/tools\/([^/]+)$/);
  if (toolMatch) {
    const tool = findTool(decodeURIComponent(toolMatch[1]));
    if (tool) {
      return metadata(
        path,
        `${tool.name} MCP Tool | BeatBandit`,
        tool.description,
        { structuredType: 'TechArticle' },
      );
    }
  }

  const promptMatch = path.match(/^\/mcp\/prompts\/([^/]+)$/);
  if (promptMatch) {
    const prompt = findPrompt(decodeURIComponent(promptMatch[1]));
    if (prompt) {
      return metadata(
        path,
        `${prompt.title} MCP Prompt | BeatBandit`,
        prompt.description,
        { structuredType: 'TechArticle' },
      );
    }
  }

  const resourceMatch = path.match(/^\/mcp\/resources\/([^/]+)$/);
  if (resourceMatch) {
    const resource = findResource(decodeURIComponent(resourceMatch[1]));
    if (resource) {
      return metadata(
        path,
        `${resource.title} MCP Resource | BeatBandit`,
        resource.description,
        { structuredType: 'TechArticle' },
      );
    }
  }

  return null;
}

export function isNoindexApplicationPath(pathname: string): boolean {
  const path = normalizePublicPath(pathname);
  return NOINDEX_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function getApplicationRouteTitle(pathname: string): string {
  const path = normalizePublicPath(pathname);
  const match = APPLICATION_ROUTE_TITLES.find(([prefix]) =>
    path === prefix || path.startsWith(`${prefix}/`)
  );
  return match?.[1] ?? 'BeatBandit';
}

export function getPrerenderPaths(): string[] {
  return [
    ...Object.keys(STATIC_PUBLIC_ROUTES),
    ...beatLibraryMovies.map((movie) => `/library/${movie.id}`),
    ...beatLibraryBeatTypes.map((beatType) => `/library/beat/${beatType.id}`),
    ...docsBundle.prompts.map((prompt) => `/mcp/prompts/${prompt.name}`),
    ...docsBundle.tools.map((tool) => `/mcp/tools/${tool.name}`),
    ...docsBundle.resources.map((resource) => `/mcp/resources/${resource.family}`),
  ];
}

export const SITE_METADATA = {
  origin: SITE_ORIGIN,
  image: DEFAULT_IMAGE,
};
